import { Box, Typography, Button, Card, useTheme, TextField, Alert, CircularProgress, IconButton, Tooltip, Snackbar, Chip, Paper, Stack } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PixIcon from '@mui/icons-material/Pix';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import InputMask from 'react-input-mask';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { visitorService } from '../../../shared/services/visitors/visitorService';
import { useQuery } from '@tanstack/react-query';
import { eventService } from '../../../shared/services/tenant/eventService';

export default function SegundaViaPage() {
    const navigate = useNavigate();
    const { id, slug } = useParams();
    const idOrSlug = slug || id;
    const theme = useTheme();
    const location = useLocation();
    
    const basePath = slug ? `/evento/${slug}` : `/public/event/${id}`;

    const { data: event } = useQuery({
        queryKey: ['event', idOrSlug],
        queryFn: () => eventService.getPublic(idOrSlug!).then(res => res.data),
        enabled: !!idOrSlug,
    });

    const initialCpf = location.state?.cpf || localStorage.getItem('lastCpf') || '';
    
    const [paymentInfo, setPaymentInfo] = useState<any>(null);
    const [cpf, setCpf] = useState(initialCpf);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [copied, setCopied] = useState(false);
    const [showPaymentOptions, setShowPaymentOptions] = useState(false);

    const buscarPagamento = async () => {
        if (loading || !event?.id) return;
        if (!cpf || cpf.replace(/\D/g, '').length < 11) {
            setError('CPF inválido');
            return;
        }
        
        setLoading(true);
        setError(null);
        setPaymentInfo(null);
        setShowPaymentOptions(false);
        try {
            const info = await visitorService.pixInfo(cpf, event.id);
            setPaymentInfo(info);
        } catch (err: any) {
            setError('Cobrança não encontrada para este CPF.');
        }
        setLoading(false);
    };

    useEffect(() => {
        if (event?.id && initialCpf && initialCpf.replace(/\D/g, '').length === 11) {
            buscarPagamento();
        }
    }, [event?.id]);

    const handleCopy = async (text: string) => {
        await navigator.clipboard.writeText(text);
        setCopied(true);
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'PAID':
            case 'RECEIVED':
            case 'CONFIRMED':
                return 'success';
            case 'PENDING':
            case 'OPEN':
                return 'warning';
            case 'EXPIRED':
            case 'OVERDUE':
                return 'error';
            default:
                return 'default';
        }
    };

    const getStatusLabel = (status: string) => {
        switch (status) {
            case 'PAID':
            case 'RECEIVED':
            case 'CONFIRMED':
                return 'Pago';
            case 'PENDING':
            case 'OPEN':
                return 'Pendente';
            case 'EXPIRED':
                return 'Expirado';
            case 'OVERDUE':
                return 'Vencido';
            default:
                return status;
        }
    };

    // Verifica se o pagamento ainda pode ser alterado
    // Só pode trocar se for PIX (criado automaticamente na inscrição) e ainda não pago
    const isPix = paymentInfo && (paymentInfo.payload || paymentInfo.billingType === 'PIX');
    const canChangePaymentMethod = paymentInfo && 
        isPix &&
        !['PAID', 'RECEIVED', 'CONFIRMED'].includes(paymentInfo.status) &&
        !paymentInfo.bankSlipUrl; // Se tem boleto, já escolheu o método

    // Métodos de pagamento disponíveis no evento
    const paymentPix = event?.payment_pix ?? true;
    const paymentCard = event?.payment_card ?? true;
    const paymentBoleto = event?.payment_boleto ?? true;

    const handleSelectPaymentMethod = (method: 'pix' | 'card' | 'boleto') => {
        const cleanCpf = cpf.replace(/\D/g, '');
        localStorage.setItem('lastCpf', cleanCpf);
        navigate(`${basePath}/${method}`, { state: { cpf: cleanCpf } });
    };

    return (
        <Box sx={{ p: { xs: 1, md: 4 }, background: theme.palette.background.default, minHeight: '100vh' }}>
            <Card sx={{
                background: theme.palette.background.paper,
                borderRadius: 4,
                p: 4,
                maxWidth: 600,
                margin: '32px auto',
                boxShadow: '0 2px 8px 0 rgba(16,30,54,0.04)',
                textAlign: 'center'
            }}>
                <ReceiptIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                <Typography variant="h4" fontWeight={700} color="primary.main" sx={{ mb: 1 }}>
                    2ª Via de Pagamento
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    Consulte o status do seu pagamento e acesse os dados para efetuar o pagamento.
                </Typography>

                {/* Campo CPF */}
                <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <InputMask
                        mask="999.999.999-99"
                        value={cpf}
                        onChange={(e) => {
                            setCpf(e.target.value);
                            setError(null);
                        }}
                    >
                        {(inputProps: any) => (
                            <TextField
                                {...inputProps}
                                label="CPF"
                                placeholder="000.000.000-00"
                                sx={{ width: 250 }}
                                size="small"
                                error={!!error}
                            />
                        )}
                    </InputMask>
                    <Button
                        variant="contained"
                        onClick={buscarPagamento}
                        disabled={loading || !event?.id}
                    >
                        {loading ? <CircularProgress size={20} /> : 'Consultar'}
                    </Button>
                </Box>

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                {/* Informações do Pagamento */}
                {paymentInfo && !showPaymentOptions && (
                    <Box sx={{ mt: 3, textAlign: 'left' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6" fontWeight={600}>
                                Status do Pagamento
                            </Typography>
                            <Chip 
                                label={getStatusLabel(paymentInfo.status)} 
                                color={getStatusColor(paymentInfo.status) as any}
                            />
                        </Box>

                        {/* Se já está pago */}
                        {['PAID', 'RECEIVED', 'CONFIRMED'].includes(paymentInfo.status) && (
                            <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2 }}>
                                Seu pagamento foi confirmado! Obrigado.
                            </Alert>
                        )}

                        {/* Botão para trocar método de pagamento */}
                        {canChangePaymentMethod && (
                            <Alert 
                                severity="info" 
                                sx={{ mb: 2 }}
                                action={
                                    <Button 
                                        color="inherit" 
                                        size="small"
                                        startIcon={<SwapHorizIcon />}
                                        onClick={() => setShowPaymentOptions(true)}
                                    >
                                        Trocar
                                    </Button>
                                }
                            >
                                Deseja pagar por outro método? Você pode alterar a forma de pagamento.
                            </Alert>
                        )}

                        {/* Se tem QR Code PIX */}
                        {paymentInfo.payload && !['PAID', 'RECEIVED', 'CONFIRMED'].includes(paymentInfo.status) && (
                            <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <PixIcon color="primary" />
                                    <Typography variant="subtitle1" fontWeight={600}>
                                        Pagar com PIX
                                    </Typography>
                                </Box>
                                
                                {paymentInfo.qrcode && (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                                        <img src={paymentInfo.qrcode} alt="QR Code PIX" style={{ maxWidth: 200 }} />
                                    </Box>
                                )}
                                
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                    Código PIX Copia e Cola:
                                </Typography>
                                <Box sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 1,
                                    bgcolor: 'white',
                                    p: 1,
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: 'divider'
                                }}>
                                    <Typography 
                                        variant="body2" 
                                        sx={{ 
                                            flex: 1, 
                                            wordBreak: 'break-all',
                                            fontFamily: 'monospace',
                                            fontSize: 11
                                        }}
                                    >
                                        {paymentInfo.payload.substring(0, 50)}...
                                    </Typography>
                                    <Tooltip title={copied ? 'Copiado!' : 'Copiar'}>
                                        <IconButton onClick={() => handleCopy(paymentInfo.payload)} size="small">
                                            {copied ? <CheckCircleIcon color="success" /> : <ContentCopyIcon />}
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </Box>
                        )}

                        {/* Se tem link do boleto */}
                        {paymentInfo.bankSlipUrl && !['PAID', 'RECEIVED', 'CONFIRMED'].includes(paymentInfo.status) && (
                            <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <ReceiptIcon color="primary" />
                                    <Typography variant="subtitle1" fontWeight={600}>
                                        Boleto Bancário
                                    </Typography>
                                </Box>
                                <Button 
                                    variant="outlined" 
                                    fullWidth
                                    href={paymentInfo.bankSlipUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Visualizar Boleto
                                </Button>
                            </Box>
                        )}

                        {/* Se tem link de invoice (cartão ou geral) */}
                        {paymentInfo.invoiceUrl && !['PAID', 'RECEIVED', 'CONFIRMED'].includes(paymentInfo.status) && (
                            <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <CreditCardIcon color="primary" />
                                    <Typography variant="subtitle1" fontWeight={600}>
                                        Link de Pagamento
                                    </Typography>
                                </Box>
                                <Button 
                                    variant="outlined" 
                                    fullWidth
                                    href={paymentInfo.invoiceUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Acessar Pagamento
                                </Button>
                            </Box>
                        )}

                        {/* Valor */}
                        {paymentInfo.value && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                                <Typography color="text.secondary">Valor:</Typography>
                                <Typography fontWeight={600}>
                                    R$ {Number(paymentInfo.value).toFixed(2).replace('.', ',')}
                                </Typography>
                            </Box>
                        )}

                        {/* Vencimento */}
                        {paymentInfo.dueDate && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                                <Typography color="text.secondary">Vencimento:</Typography>
                                <Typography fontWeight={600}>
                                    {new Date(paymentInfo.dueDate + 'T00:00:00').toLocaleDateString('pt-BR')}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                )}

                {/* Opções de método de pagamento */}
                {showPaymentOptions && (
                    <Box sx={{ mt: 3 }}>
                        <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                            Escolha a forma de pagamento
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Selecione como deseja pagar. Uma nova cobrança será gerada.
                        </Typography>

                        <Stack spacing={2}>
                            {paymentBoleto && (
                                <Paper 
                                    sx={{ 
                                        p: 2,
                                        cursor: 'pointer',
                                        border: `2px solid ${theme.palette.divider}`,
                                        '&:hover': {
                                            border: `2px solid ${theme.palette.primary.main}`,
                                        },
                                        transition: 'all 0.2s'
                                    }}
                                    onClick={() => handleSelectPaymentMethod('boleto')}
                                >
                                    <Stack direction="row" alignItems="center" spacing={2}>
                                        <ReceiptIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                                        <Box>
                                            <Typography variant="subtitle1" fontWeight={600}>
                                                Pagamento Parcelado
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                PIX ou Boleto até a data da viagem
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Paper>
                            )}

                            {paymentCard && (
                                <Paper 
                                    sx={{ 
                                        p: 2,
                                        cursor: 'pointer',
                                        border: `2px solid ${theme.palette.divider}`,
                                        '&:hover': {
                                            border: `2px solid ${theme.palette.primary.main}`,
                                        },
                                        transition: 'all 0.2s'
                                    }}
                                    onClick={() => handleSelectPaymentMethod('card')}
                                >
                                    <Stack direction="row" alignItems="center" spacing={2}>
                                        <CreditCardIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                                        <Box>
                                            <Typography variant="subtitle1" fontWeight={600}>
                                                Cartão de Crédito
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Parcelamento disponível
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Paper>
                            )}

                            {paymentPix && (
                                <Paper 
                                    sx={{ 
                                        p: 2,
                                        cursor: 'pointer',
                                        border: `2px solid ${theme.palette.divider}`,
                                        '&:hover': {
                                            border: `2px solid ${theme.palette.primary.main}`,
                                        },
                                        transition: 'all 0.2s'
                                    }}
                                    onClick={() => handleSelectPaymentMethod('pix')}
                                >
                                    <Stack direction="row" alignItems="center" spacing={2}>
                                        <PixIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                                        <Box>
                                            <Typography variant="subtitle1" fontWeight={600}>
                                                PIX à Vista
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Pagamento instantâneo
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Paper>
                            )}
                        </Stack>

                        <Button
                            variant="text"
                            sx={{ mt: 2 }}
                            onClick={() => setShowPaymentOptions(false)}
                        >
                            Voltar
                        </Button>
                    </Box>
                )}

                <Button
                    variant="text"
                    color="primary"
                    sx={{ mt: 3 }}
                    onClick={() => navigate(basePath)}
                >
                    Voltar para o evento
                </Button>
            </Card>

            <Snackbar
                open={copied}
                autoHideDuration={2000}
                onClose={() => setCopied(false)}
                message="Código copiado!"
            />
        </Box>
    );
}
